import type { Attachment, BusinessOperatingSystem, SortDirection, UserModel } from '@ninety/mobile/platform/api';

export enum ValueRating {
  POSITIVE = '+',
  SOMETIMES = '+/-',
  NEGATIVE = '-',
  EMPTY = '',
}

export enum YesNo {
  YES = 'Yes',
  NO = 'No',
  EMPTY = '',
}

export enum YesNoMaybe {
  YES = 'Yes',
  NO = 'No',
  MAYBE = 'Maybe',
  EMPTY = '',
}

export enum DiscussionCommitmentType {
  leadership = 'leadership',
  coaching = 'coaching',
}

export enum DiscussionRepetitionFrequency {
  none = 'none',
  weekly = 'weekly',
  biweekly = 'biweekly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  annually = 'annually',
}

export enum DiscussionRockStatusCode {
  OFF_TRACK = '0000',
  ON_TRACK = '0001',
  DONE = '0002',
  AT_RISK = '0003',
}

export enum DiscussionType {
  quarterly = 'quarterly',
  annual = 'annual',
}

export enum DiscussionSortField {
  none = 'None',
  title = 'title',
  dueDate = 'dueDate',
  completedDate = 'completedDate',
  manageeFullName = 'manageeFullName',
  managerFullName = 'managerFullName',
}

export enum DiscussionMeasurableCurrency {
  /** $ */
  USD = 'USD',

  /** € */
  EUR = 'EUR',

  /** £ */
  GBP = 'GBP',
}

export enum DiscussionMeasurableUnit {
  number = 'number',
  percentage = 'percentage',
  dollar = 'dollar',
  euro = 'euro',
  pound = 'pound',
  yesno = 'yesno',
  time = 'time',
}

export enum DiscussionMeasurableGoalOrientation {
  gt = 'gt',
  gte = 'gte',
  lt = 'lt',
  lte = 'lte',
  equal = 'equal',
  inside = 'inside',
  outside = 'outside',
}

export interface DiscussionDefaultQuestionUpdate {
  question?: string;
  elementId?: string;
  title?: string;
  sectionTitleSameAsQuestion?: boolean;
}

export interface DiscussionDefaultFormalQuestionsUpdateRequest {
  defaultConversationQuestions: DiscussionDefaultQuestionUpdate[];
}

export interface DiscussionFormalQuestionsUpdateRequest {
  meetingQuestions: DiscussionFormalQuestionUpdate[];
}

export interface DiscussionOrdinalsUpdateModel {
  _id: string;
  ordinal: number;
}

export interface DiscussionOrdinalsUpdateRequest {
  models: DiscussionOrdinalsUpdateModel[];
  companyId?: string;
  templateId?: string;
}

export interface DiscussionMeasurableGoal {
  min?: number;
  max?: number;
  value: number;
  orientation: DiscussionMeasurableGoalOrientation;
}

export interface DiscussionScore {
  _id?: string;
  companyId?: string;
  periodStartDate?: Date;
  value?: number;
  goal?: DiscussionMeasurableGoal;
}

export interface DiscussionMeasurable {
  _id?: string;
  companyId?: string;
  defaultGoal?: DiscussionMeasurableGoal;
  description?: string;
  title?: string;
  unit?: DiscussionMeasurableUnit;
  currency?: DiscussionMeasurableCurrency;
  scores?: DiscussionScore[];
}

export interface DiscussionScorecard {
  headers: string[];
  measurables: DiscussionMeasurable[];
}

export interface DiscussionKpiPeriodScorecard {
  weekly: DiscussionScorecard | null;
  monthly: DiscussionScorecard | null;
  quarterly: DiscussionScorecard | null;
  annual: DiscussionScorecard | null;
}

export interface DiscussionMetadataUpdateRequest {
  /** Discussion ID */
  _id?: string;
  kpiPeriods?: DiscussionKpiPeriodScorecard;
}

export interface DiscussionPatchRequest {
  title?: string;
  description?: string;
  periodStartDate?: Date;
  periodEndDate?: Date;
  dueDate?: Date;
  canEvaluateSelf?: boolean;
  canEvaluateManager?: boolean;
  holdingMeeting?: boolean;
  revealAnswersPriorToMeeting?: boolean;
  isCompleted?: boolean;
  isDeleted?: boolean;
  rocks?: DiscussionRock[];
  comments: DiscussionComment[];
}

export interface DiscussionTodoStats {
  completedOnTime: number;
  notCompletedOnTime: number;
  pastDue: number;
  incomplete: number;
  total: number;
}

export interface DiscussionFormUpdate {
  coreValueRatings?: DiscussionCoreValueRating[];
  roleRatings?: DiscussionRoleRating[];
  // seatRating?: DiscussionSeatRating; // Commented out on BE
  coreValueNotes?: string;
  roleNotes?: string;
  rockNotes?: string;
  manageeGeneralNotes?: string;
  managerGeneralNotes?: string;
  manageeLeadershipNotes?: string;
  managerLeadershipNotes?: string;
  leadershipAssessment?: DiscussionLeadershipAssessment;
  managementAssessment?: DiscussionManagementAssessment;
  leadershipCommitments?: DiscussionCommitment[];
  coachingCommitments?: DiscussionCommitment[];
}

export interface DiscussionFormalQuestionUpdate {
  manageeAnswer?: string;
  managerAnswer?: string;
}

export interface DiscussionUpdateRequest {
  title?: string;
  description?: string;
  periodStartDate?: Date;
  periodEndDate?: Date;
  dueDate?: Date;
  canEvaluateSelf?: boolean;
  canEvaluateManager?: boolean;
  holdingMeeting?: boolean;
  revealAnswersPriorToMeeting?: boolean;
  isCompleted?: boolean;
  isDeleted?: boolean;
  rocks?: DiscussionRock[];
  comments?: DiscussionComment[];
  meetingQuestions?: DiscussionFormalQuestionUpdate[];
  quarterlyQuestions?: DiscussionFormalQuestionUpdate[];
  isArchived?: boolean;
  archivedDate?: Date;
  hasManageeStarted?: boolean;
  hasManagerStarted?: boolean;
  isManageeDone?: boolean;
  isManagerDone?: boolean;
  completedDate?: Date;
  managerDiscussionForm?: DiscussionFormUpdate;
  manageeDiscussionForm?: DiscussionFormUpdate;
  managerSignature?: DiscussionSignature;
  manageeSignature?: DiscussionSignature;
  isMeetingCompleted?: boolean;
  todosStats?: DiscussionTodoStats;
  repeats?: DiscussionRepetitionFrequency;
  nextConversationId?: string;
  version?: number;
}

export class DiscussionListRequest {
  /** Defaults to false on back end */
  isArchived?: boolean;

  /** Defaults to 'quarterly' */
  type = DiscussionType.quarterly;

  /** Defaults to 0 */
  page = 0;

  /** Defaults to 10 */
  take = 10;

  /** Defaults to 'None' on back end */
  sortField?: DiscussionSortField;

  /** Defaults to 'DESC' on back end */
  sortDirection?: SortDirection;

  /** Not required */
  searchText?: string;

  /** Defaults to 'null' on back end */
  isCompleted?: boolean;

  /** Required value or else 400 error */
  selectedUserId = 'all';
}

export interface DiscussionManagee {
  seatHolderId: string;
  userId: string;
  dueDate: Date;
}

export interface DiscussionCreateRequest {
  title: string;
  managerUserId: string;
  managees: DiscussionManagee[];
  periodStartDate: Date;
  periodEndDate: Date;
  canEvaluateSelf: boolean;
  canEvaluateManager: boolean;
  shouldSendEmailNotifications: boolean;
  holdingMeeting: boolean;
  type: DiscussionType;
  repeat: DiscussionRepetitionFrequency;
  version?: number;
  description?: string;
  revealAnswersPriorToMeeting?: boolean;
  createTodos?: boolean;
}

export interface DiscussionRock {
  dueDate: string;
  rockId: string;
  description: string;
  title: string;
  statusCode: DiscussionRockStatusCode;
}

export interface DiscussionFormalQuestion {
  question: string;
  elementId: string;
  title: string;
  sectionTitleSameAsQuestion: boolean;
  manageeAnswer: string;
  managerAnswer: string;
}

export interface DiscussionSignature {
  signature: string;
  date?: Date;
}

export interface DiscussionSeatHolder {
  _id: string;
  chartId: string;
  companyId: string;
  userId: string;
  seatId: string;
  managerSeatId: string;
  deleted: boolean;
  ordinal?: number;
}

export interface CommitmentItem {
  label: string;
  subItems?: string[];
}

export interface CommitmentLanguage {
  title: string;
  items: CommitmentItem[];
}

export interface DiscussionCommitment {
  commitmentType: DiscussionCommitmentType;
  bos: BusinessOperatingSystem;
  manager: CommitmentLanguage;
  managee: CommitmentLanguage;
  amk?: string;
}

export interface DiscussionCommitmentRating {
  rating: YesNoMaybe;
  commitment: DiscussionCommitment;
}

export interface DiscussionManagementAssessment {
  expectations: YesNo;
  communicating: YesNo;
  pulse: YesNo;
  quarterlyConversations: YesNo;
  rewarding: YesNo;
  note: string;
  expectationsIntoAgreements: YesNoMaybe;
  communicateWell: YesNoMaybe;
  meetingCadence: YesNoMaybe;
  howWereDoing: YesNoMaybe;
  workTogether: YesNoMaybe;
  appreciateEachOther: YesNoMaybe;
}

export interface DiscussionLeadershipAssessment {
  /** Unused on BE */
  direction: YesNo;
  /** Unused on BE */
  tools: YesNo;
  /** Unused on BE */
  vine: YesNo;
  /** Unused on BE */
  greaterGood: YesNo;
  /** Unused on BE */
  clarityBreaks: YesNo;
  /** Tech Debt: This is the only property currently used in this object on the BE.
            A data migration would probably be needed to clean this up. */
  note: string;
  /** Unused on BE */
  compellingVision: YesNoMaybe;
  /** Unused on BE */
  provideResources: YesNoMaybe;
  /** Unused on BE */
  focusOnWhatNotHow: YesNoMaybe;
  /** Unused on BE */
  honorCoreAgreements: YesNoMaybe;
  /** Unused on BE */
  leadByExample: YesNoMaybe;
  /** Unused on BE */
  openAndTransparent: YesNoMaybe;
}

export interface DiscussionSeatRating {
  getsIt: YesNoMaybe;
  wantsIt: YesNoMaybe;
  hasCapacity: YesNoMaybe;
}

export interface DiscussionRole {
  roleId: string;
  name: string;
  description: string;
}

/** Tech Debt: This model has a different shape in the DB than it appears elsewhere. */
export interface DiscussionRoleRating {
  getsIt: YesNoMaybe;
  wantsIt: YesNoMaybe;
  hasCapacity: YesNoMaybe;
  role: DiscussionRole;
}

export interface DiscussionCoreValue {
  coreValueId: string;
  name: string;
  description: string;
}

/** Tech Debt: This model has a diffferent shape in the DB than it appears elsewhere. */
export interface DiscussionCoreValueRating {
  rating: ValueRating;
  coreValue: DiscussionCoreValue;
}

export interface DiscussionForm {
  coreValueRatings: DiscussionCoreValueRating[];
  roleRatings: DiscussionRoleRating[];
  seatRating: DiscussionSeatRating;
  coreValueNotes: string;
  roleNotes: string;
  rockNotes: string;
  manageeGeneralNotes?: string;
  managerGeneralNotes: string;
  manageeLeadershipNotes?: string;
  managerLeadershipNotes: string;
  leadershipAssessment?: DiscussionLeadershipAssessment;
  managementAssessment?: DiscussionManagementAssessment;
  leadershipCommitments: DiscussionCommitmentRating[];
  coachingCommitments: DiscussionCommitmentRating[];
}

export type DiscussionParticipantFormKey = keyof Pick<
  DiscussionModel,
  'managerDiscussionForm' | 'manageeDiscussionForm'
>;

export interface DiscussionComment {
  _id?: string;
  userId?: string;
  personId?: string;
  text: string;
  createdDate: string;
  editedDate?: string;
}

export interface DiscussionListItem {
  _id: string;
  attachments?: Attachment[];
  bos: BusinessOperatingSystem;
  canEvaluateManager?: boolean;
  canEvaluateSelf?: boolean;
  comments?: DiscussionComment[];
  completedDate?: Date;
  dueDate?: Date;
  hasManageeConversation?: boolean;
  hasManageeStarted?: boolean;
  hasManagerConversation?: boolean;
  hasManagerStarted?: boolean;
  holdingMeeting?: boolean;
  isCompleted?: boolean;
  isDone?: boolean;
  isManageeDone?: boolean;
  isManagerDone?: boolean;
  isMeetingCompleted?: boolean;
  managee?: UserModel;
  manageeFullName: string;
  manageeUserId?: string;
  manager?: UserModel;
  managerFullName: string;
  managerUserId?: string;
  revealAnswersPriorToMeeting?: boolean;
  title?: string;
  showStartMeetingButton?: boolean;
}

export interface DiscussionTodoStats {
  completedOnTime: number;
  notCompletedOnTime: number;
  pastDue: number;
  incomplete: number;
}

export interface DiscussionLanguage {
  CCC: string;
  competency: string;
  commitment: string;
  capacity: string;
  leadershipCommitments: string;
  coachingCommitments: string;
  rockItems: string;
  LAC: string;
}

export interface DiscussionModel extends DiscussionListItem {
  companyId: string;
  description: string;
  isArchived: boolean;
  isDeleted: boolean;
  manageeDiscussionForm?: DiscussionForm;
  manageeSeatholder: DiscussionSeatHolder;
  manageeSeatholderId: string;
  manageeSignature?: DiscussionSignature;
  managerDiscussionForm?: DiscussionForm;
  managerSignature?: DiscussionSignature;
  meetingQuestions: DiscussionFormalQuestion[];
  nextConversationId?: string;
  periodEndDate: Date;
  periodStartDate: Date;
  quarterlyQuestions: DiscussionFormalQuestion[];
  repeats: DiscussionRepetitionFrequency;
  rocks: DiscussionRock[];
  seatDescription: string;
  seatName: string;
  shouldSendEmailNotifications: boolean;
  type: DiscussionType;
  version?: number;
  todoStats?: DiscussionTodoStats;
  conversationLanguage?: DiscussionLanguage;
}

export enum DiscussionDetailTab {
  Overview = 'Overview',
  Settings = 'Settings',
}
